import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Input, Button, Card, Select, Space, App, Modal, Form, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, GlobalOutlined } from '@ant-design/icons';
import apiClient from '../../api/axios';

interface TranslationItem {
    key: string;
    translations: {
        de: string;
        en: string;
        tr: string;
        ru: string;
        ar: string;
    };
}

export const Translations: React.FC = () => {
    const queryClient = useQueryClient();
    const { message, modal } = App.useApp();
    const [client, setClient] = useState<'Mosaik' | 'Ole'>('Mosaik');
    const [searchText, setSearchText] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<TranslationItem | null>(null);
    const [form] = Form.useForm();
    const [pageSize, setPageSize] = useState(15);

    // Fetch translations
    const { data: translations = [], isLoading } = useQuery<TranslationItem[]>({
        queryKey: ['adminTranslations', client],
        queryFn: async () => {
            const response = await apiClient.get(`/admin/translations?client=${client}`);
            return response.data.data || [];
        }
    });

    // Save (Create/Update) mutation
    const saveMutation = useMutation({
        mutationFn: async (values: { key: string; translations: Record<string, string> }) => {
            const payload = {
                client,
                key: values.key,
                translations: values.translations
            };
            await apiClient.post('/admin/translations', payload);
        },
        onSuccess: () => {
            message.success('Çeviri başarıyla kaydedildi.');
            setIsEditModalOpen(false);
            form.resetFields();
            setEditingItem(null);
            queryClient.invalidateQueries({ queryKey: ['adminTranslations', client] });
        },
        onError: (err: any) => {
            console.error(err);
            message.error('Çeviri kaydedilemedi.');
        }
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (key: string) => {
            await apiClient.delete(`/admin/translations?client=${client}&key=${key}`);
        },
        onSuccess: () => {
            message.success('Çeviri anahtarı silindi.');
            queryClient.invalidateQueries({ queryKey: ['adminTranslations', client] });
        },
        onError: (err: any) => {
            console.error(err);
            message.error('Çeviri anahtarı silinemedi.');
        }
    });

    const handleEdit = (record: TranslationItem) => {
        setEditingItem(record);
        form.setFieldsValue({
            key: record.key,
            translations: record.translations
        });
        setIsEditModalOpen(true);
    };

    const handleCreate = () => {
        setEditingItem(null);
        form.resetFields();
        setIsEditModalOpen(true);
    };

    const handleDelete = (key: string) => {
        modal.confirm({
            title: 'Çeviriyi Sil',
            content: `"${key}" anahtarını ve tüm dillerdeki çevirilerini silmek istediğinize emin misiniz?`,
            okText: 'Evet, Sil',
            okType: 'danger',
            cancelText: 'Vazgeç',
            onOk: () => {
                deleteMutation.mutate(key);
            }
        });
    };

    const handleFormSubmit = (values: any) => {
        saveMutation.mutate(values);
    };

    // Filter local translations by search query
    const filteredTranslations = translations.filter(item => 
        item.key.toLowerCase().includes(searchText.toLowerCase()) ||
        Object.values(item.translations).some(val => val.toLowerCase().includes(searchText.toLowerCase()))
    );

    const columns = [
        {
            title: 'Anahtar (Key)',
            dataIndex: 'key',
            key: 'key',
            sorter: (a: TranslationItem, b: TranslationItem) => a.key.localeCompare(b.key),
            width: '25%',
        },
        {
            title: 'Almanca (de)',
            dataIndex: ['translations', 'de'],
            key: 'de',
            ellipsis: true,
        },
        {
            title: 'İngilizce (en)',
            dataIndex: ['translations', 'en'],
            key: 'en',
            ellipsis: true,
        },
        {
            title: 'Türkçe (tr)',
            dataIndex: ['translations', 'tr'],
            key: 'tr',
            ellipsis: true,
        },
        {
            title: 'Rusça (ru)',
            dataIndex: ['translations', 'ru'],
            key: 'ru',
            ellipsis: true,
        },
        {
            title: 'Arapça (ar)',
            dataIndex: ['translations', 'ar'],
            key: 'ar',
            ellipsis: true,
            className: 'text-right',
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 120,
            render: (_: any, record: TranslationItem) => (
                <Space size="middle">
                    <Tooltip title="Düzenle">
                        <Button 
                            icon={<EditOutlined />} 
                            onClick={() => handleEdit(record)} 
                            type="text" 
                        />
                    </Tooltip>
                    <Tooltip title="Sil">
                        <Button 
                            icon={<DeleteOutlined />} 
                            onClick={() => handleDelete(record.key)} 
                            danger 
                            type="text" 
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-4" style={{ maxWidth: '100%' }}>
            <Card 
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <Space size="middle">
                            <GlobalOutlined style={{ fontSize: '20px', color: '#1677ff' }} />
                            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Dil ve Çeviri Yönetimi</span>
                        </Space>
                        <Select 
                            value={client} 
                            onChange={(value) => { setClient(value); setSearchText(''); }}
                            style={{ width: 200 }}
                            options={[
                                { value: 'Mosaik', label: 'Mosaik Web Sitesi' },
                                { value: 'Ole', label: 'OLE Lernzentrum' }
                            ]}
                        />
                    </div>
                }
                className="shadow-sm border-0 rounded-xl"
            >
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <Input
                        placeholder="Anahtar veya çeviri metninde ara..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ maxWidth: 350 }}
                        allowClear
                    />
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleCreate}
                    >
                        Yeni Çeviri Ekle
                    </Button>
                </div>

                <Table 
                    columns={columns} 
                    dataSource={filteredTranslations} 
                    rowKey="key" 
                    loading={isLoading}
                    pagination={{ 
                        pageSize: pageSize, 
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '15', '20', '50', '100'],
                        onChange: (_, size) => setPageSize(size)
                    }}
                    bordered
                    size="middle"
                />
            </Card>

            <Modal
                title={editingItem ? 'Çeviriyi Düzenle' : 'Yeni Çeviri Ekle'}
                open={isEditModalOpen}
                onCancel={() => setIsEditModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFormSubmit}
                    initialValues={{
                        translations: { de: '', en: '', tr: '', ru: '', ar: '' }
                    }}
                >
                    <Form.Item
                        name="key"
                        label="Çeviri Anahtarı (Key)"
                        rules={[
                            { required: true, message: 'Lütfen çeviri anahtarını girin!' },
                            { pattern: /^[a-zA-Z0-9_.-]+$/, message: 'Yalnızca harf, sayı, nokta, alt çizgi ve tire kullanılabilir.' }
                        ]}
                    >
                        <Input 
                            placeholder="Örn: common.save" 
                            disabled={!!editingItem} // Key is immutable on edit
                        />
                    </Form.Item>

                    <Form.Item label="Almanca (de)" name={['translations', 'de']} rules={[{ required: true, message: 'Almanca çeviri zorunludur (Sistem varsayılanı)!' }]}>
                        <Input.TextArea placeholder="Almanca metin..." autoSize={{ minRows: 2, maxRows: 6 }} />
                    </Form.Item>

                    <Form.Item label="İngilizce (en)" name={['translations', 'en']}>
                        <Input.TextArea placeholder="İngilizce metin..." autoSize={{ minRows: 2, maxRows: 6 }} />
                    </Form.Item>

                    <Form.Item label="Türkçe (tr)" name={['translations', 'tr']}>
                        <Input.TextArea placeholder="Türkçe metin..." autoSize={{ minRows: 2, maxRows: 6 }} />
                    </Form.Item>

                    <Form.Item label="Rusça (ru)" name={['translations', 'ru']}>
                        <Input.TextArea placeholder="Rusça metin..." autoSize={{ minRows: 2, maxRows: 6 }} />
                    </Form.Item>

                    <Form.Item label="Arapça (ar)" name={['translations', 'ar']}>
                        <Input.TextArea placeholder="Arapça metin..." autoSize={{ minRows: 2, maxRows: 6 }} style={{ direction: 'rtl' }} />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setIsEditModalOpen(false)}>İptal</Button>
                            <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
                                Kaydet
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
